import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { BarChart3, Boxes, FileText, Wallet } from "lucide-react";

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Pie,
  Cell,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../../lib/api";
import { formatInr, formatInrLoose } from "../../lib/currency";
import { useAuth } from "../../providers/AuthProvider";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ReportsDateRangeFilterBar } from "../components/reports/ReportsDateRangeFilterBar";

type SalesSummaryData = {
  total_revenue: string;
  total_cost: string;
  total_orders: number;
  total_items_sold: string;
  avg_order_value: string;
  total_profit: string;
};

type SalesTrendRow = {
  date: string;
  total_sales: string;
};

function parseMoney(v: string) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function ReportsPage() {
  const { token } = useAuth();

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [salesSummary, setSalesSummary] = useState<SalesSummaryData | null>(null);
  const [salesTrend, setSalesTrend] = useState<SalesTrendRow[]>([]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const [sumRes, trendRes] = await Promise.all([
          api.reports.getSalesSummary({
            token,
            filters: { from_date: fromDate, to_date: toDate },
          }),
          api.reports.getSalesTrend({
            token,
            filters: { from_date: fromDate, to_date: toDate },
          }),
        ]);

        if (cancelled) return;

        setSalesSummary(sumRes.data as SalesSummaryData);
        setSalesTrend((trendRes.data as SalesTrendRow[]) ?? []);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load report summary");
        setSalesSummary(null);
        setSalesTrend([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [token, fromDate, toDate]);

  const totals = useMemo(() => {
    const totalSales = salesSummary ? parseMoney(salesSummary.total_revenue) : 0;
    const totalInvestment = salesSummary ? parseMoney(salesSummary.total_cost) : 0;
    const totalProfit = salesSummary ? parseMoney(salesSummary.total_profit) : 0;
    return { totalSales, totalInvestment, totalProfit };
  }, [salesSummary]);

  const trendChartData = useMemo(() => {
    return salesTrend.map((r) => ({
      date: r.date,
      total_sales: parseMoney(r.total_sales),
    }));
  }, [salesTrend]);

  const pieChartData = useMemo(() => {
    const salesValue = totals.totalSales;
    const investmentValue = totals.totalInvestment;
    const profitValue = totals.totalProfit;

    const isProfit = profitValue >= 0;
    const profitLabel = isProfit ? "Profit" : "Loss";
    const profitAbs = Math.abs(profitValue);

    return [
      { name: "Sales", value: salesValue, color: "#2563eb" },
      { name: "Capital", value: investmentValue, color: "var(--chart-1)" },
      { name: profitLabel, value: profitAbs, color: isProfit ? "#f59e0b" : "#ef4444" },
    ];
  }, [totals.totalSales, totals.totalInvestment, totals.totalProfit]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Reports</h1>
          <p className="text-muted-foreground">Report summary powered by your selected date range</p>
        </div>

        <ReportsDateRangeFilterBar
          fromDate={fromDate}
          toDate={toDate}
          onChange={({ fromDate: nextFrom, toDate: nextTo }) => {
            setFromDate(nextFrom);
            setToDate(nextTo);
          }}
        />
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total sales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{loading ? "..." : formatInr(totals.totalSales)}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Total investment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {loading ? "..." : formatInr(totals.totalInvestment)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total profit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {loading ? "..." : formatInr(totals.totalProfit)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Graph prominence recommendation:
          - Make the Sales Trend line chart the primary visualization (most actionable: revenue pattern over time).
          - Use a smaller totals comparison bar chart as secondary context. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm xl:col-span-2">
          <CardHeader>
            <div>
              <CardTitle>Sales trend</CardTitle>
              <CardDescription>Primary chart: daily revenue totals for the selected date range.</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-muted-foreground">Loading chart...</div>
            ) : trendChartData.length === 0 ? (
              <div className="text-muted-foreground">No sales data for the selected range.</div>
            ) : (
              <div className="h-[280px] min-h-[240px] w-full min-w-0 md:h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      minTickGap={10}
                    />
                    <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => formatInrLoose(Number(v))} />
                    <Tooltip
                      formatter={(value: number) => formatInrLoose(value)}
                      labelFormatter={(label) => label}
                    />
                    <Line type="monotone" dataKey="total_sales" stroke="#2563eb" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div>
              <CardTitle>Totals snapshot</CardTitle>
              <CardDescription>Secondary chart: compare sales vs investment vs profit (or loss).</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-muted-foreground">Loading chart...</div>
            ) : (
              <div className="h-[220px] min-h-[200px] w-full min-w-0 md:h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      formatter={(value: number, name: string) => [formatInr(value), name]}
                      labelFormatter={(label) => label}
                    />
                    <Pie
                      data={pieChartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={95}
                      paddingAngle={2}
                      isAnimationActive={false}
                      label={(entry) => `${entry.name}`}
                    />
                    {pieChartData.map((entry, idx) => (
                      <Cell key={`slice-${idx}`} fill={entry.color} />
                    ))}
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Business reports
          </CardTitle>
          <CardDescription>Sales, product performance, and inventory overview.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="shadow-none border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Sales
                </CardTitle>
                <CardDescription>Summary + daily trend.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild>
                  <Link to="/reports/sales">Open sales reports</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-none border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Products
                </CardTitle>
                <CardDescription>Performance by revenue.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline">
                  <Link to="/reports/products">Open product performance</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-none border border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Boxes className="w-4 h-4" />
                  Inventory
                </CardTitle>
                <CardDescription>Stock summary and value.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline">
                  <Link to="/reports/inventory">Open inventory summary</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
